import React, { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CreditApplication, CreditSubmission, Proposal, VendorOpportunity } from '@trnsact/trnsact-shared-types';
import { CREDIT_SUB_STAGE_OPTIONS } from 'modules/creditSubmission/constants';
import { getApprovalDetailsActiveStep } from 'modules/creditSubmission/lib';
import { LenderApproval } from 'components/CompareApprovals/types';
import { SelectField } from 'components/form/rhf/SelectField';
import { useCreditSubmission } from 'modules/creditSubmission/hooks/useCreditSubmission';
import { useNotifications } from 'modules/notification';
import { approvalNotesSchema } from '../../model';
import { useSubmissionDocuments } from '../../hooks/useSubmissionDocuments';
import { ApprovalNotesFormValues } from '../../types';
import { DecisionSteps } from './DecisionSteps';
import { ApprovalOverviewCard } from './ApprovalOverviewCard';
import { SectionCard } from './SectionCard';
import { DecisionLetterTable } from './DecisionLetterTable';
import { SubmissionDocumentsTable } from './SubmissionDocumentsTable';
import LenderSubmissionFileUpLoadModal from 'pages/VendorOpportunity/LenderSubmissionFileUploadModal';
import SpecificTasks from 'pages/VendorOpportunity/SpecificTasks';

const UntypedSpecificTasks = SpecificTasks as React.ComponentType<any>;

interface Props {
  creditSubmission: CreditSubmission & { proposals?: Proposal[]; contingencies: string[] };
  creditApp: CreditApplication;
  vo: VendorOpportunity;
  approval: LenderApproval | null;
  onBack: () => void;
}

const toContingenciesText = (contingencies: CreditSubmission['contingencies']) =>
  Array.isArray(contingencies) ? contingencies.join('\n') : (contingencies ?? '');

export const ApprovalDetailsView = ({ creditSubmission, creditApp, vo, approval, onBack }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dynamicsCreditSubmissionId = creditSubmission.creditSubId!;
  const [uploadModalType, setUploadModalType] = useState<'decisionLetter' | 'submissionDocs' | null>(null);

  const vp = useSelector((state: any) => state.vp);
  const account = useSelector((state: any) => state.account);
  const userProfile = useSelector((state: any) => state.userProfile);

  const {
    isLoading: isDocumentsLoading,
    decisionLetters,
    csDocuments,
    toggleLenderAccess,
    refetch,
  } = useSubmissionDocuments({
    creditSubId: dynamicsCreditSubmissionId,
    voId: vo.vendorOpportunityId!,
    creditSubmissionId: creditSubmission.creditSubmissionId ?? undefined,
  });
  const lastSavedRef = useRef<ApprovalNotesFormValues>({
    transactionSummary: creditSubmission.transactionSummary ?? '',
    lenderNotes: creditSubmission.notes ?? '',
    dealerNotes: toContingenciesText(creditSubmission.contingencies),
    creditSubStage: creditSubmission.creditSubStage ?? null,
  });

  const { handleUpdateCreditSubmission } = useCreditSubmission();
  const { showNotification } = useNotifications();

  const { control, reset } = useForm<ApprovalNotesFormValues>({
    resolver: zodResolver(approvalNotesSchema),
    defaultValues: {
      transactionSummary: creditSubmission.transactionSummary ?? '',
      lenderNotes: creditSubmission.notes ?? '',
      dealerNotes: toContingenciesText(creditSubmission.contingencies),
      creditSubStage: creditSubmission.creditSubStage ?? null,
    },
  });

  const creditSubStage = useWatch({ control, name: 'creditSubStage' });
  const activeStep = getApprovalDetailsActiveStep(creditSubmission.creditSubStage);

  useEffect(() => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    const nextValues: ApprovalNotesFormValues = {
      transactionSummary: creditSubmission.transactionSummary ?? '',
      lenderNotes: creditSubmission.notes ?? '',
      dealerNotes: toContingenciesText(creditSubmission.contingencies),
      creditSubStage: creditSubmission.creditSubStage ?? null,
    };
    reset(nextValues);
    lastSavedRef.current = nextValues;
  }, [creditSubmission, reset]);

  const saveFieldOnBlur = async (
    fieldName: 'transactionSummary' | 'lenderNotes' | 'dealerNotes' | 'creditSubStage',
    value: string | number | null | undefined,
    successMessage: string
  ) => {
    const savedValue = lastSavedRef.current?.[fieldName];
    if (savedValue === value) return;

    let payload: Record<string, any>;

    if (fieldName === 'transactionSummary') {
      payload = { transactionSummary: value };
    } else if (fieldName === 'lenderNotes') {
      payload = { notes: value };
    } else if (fieldName === 'dealerNotes') {
      payload = { contingencies: value ? [value] : null };
    } else {
      payload = { creditSubStage: value };
    }

    try {
      await handleUpdateCreditSubmission(dynamicsCreditSubmissionId, payload);
      lastSavedRef.current = { ...lastSavedRef.current, [fieldName]: value as any };
      showNotification(successMessage);
    } catch {
      showNotification('Something went wrong while saving', { type: 'error' });
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{ display: 'flex', flexDirection: 'column', gap: 4, p: 3, bgcolor: 'background.paper' }}
    >
      <Stack gap={2}>
        <Box>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none' }}>
            Back to Submissions list
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span">
            {creditApp.businessName}
          </Typography>
          <Chip
            icon={<DoneAllIcon sx={{ fontSize: 20 }} />}
            label="Approved"
            size="small"
            sx={{
              backgroundColor: '#E8F5E9',
              color: '#43A047',
              fontWeight: 500,
              fontSize: 14,
              border: '1px solid #A5D6A7',
              borderRadius: '4px',
              '& .MuiChip-icon': { color: '#43A047' },
            }}
          />
          {creditSubmission.submissionDate && (
            <Typography variant="body1" color="text.secondary" sx={{ ml: 'auto' }}>
              Submission Date: {creditSubmission.submissionDate}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography variant="subtitle2" component="span">
              Decision
            </Typography>
            <DecisionSteps activeStep={activeStep} />
          </Box>

          <Box>
            <SelectField
              name="creditSubStage"
              label="Stage"
              control={control}
              options={CREDIT_SUB_STAGE_OPTIONS}
              variant="outlined"
              sx={{ width: 300 }}
              inputProps={{
                size: 'small',
                variant: 'outlined',
                onBlur: () => saveFieldOnBlur('creditSubStage', creditSubStage, 'Stage saved'),
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1.25,
            backgroundColor: 'rgba(227, 242, 253, 0.5)',
            borderRadius: 1,
            border: '1px solid #BBDEFB',
          }}
        >
          <InfoOutlinedIcon sx={{ color: 'primary.main', flexShrink: 0 }} fontSize="small" />
          <Typography variant="body2" color="primary" sx={{ flex: 1 }}>
            Approval terms, financing options, and lender conditions are available in the Approval Overview below.
          </Typography>
        </Box>
      </Stack>

      <ApprovalOverviewCard
        vo={vo}
        approval={approval}
        creditSubmission={creditSubmission}
        formControl={control}
        onSaveField={saveFieldOnBlur}
      />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
        <Stack gap={2} sx={{ flex: 1, minWidth: 0 }}>
          <SectionCard
            title="Decision Letter"
            actionBtn={
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadModalType('decisionLetter')}
              >
                Upload document
              </Button>
            }
            emptyMessage={
              !isDocumentsLoading && !decisionLetters.length ? 'No Decision Letter uploaded yet' : undefined
            }
          >
            {(isDocumentsLoading || decisionLetters.length > 0) && (
              <DecisionLetterTable decisionLetters={decisionLetters} isLoading={isDocumentsLoading} />
            )}
          </SectionCard>
          <SectionCard
            title="Credit Submission Documents"
            actionBtn={
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadModalType('submissionDocs')}
              >
                Upload document
              </Button>
            }
            emptyMessage={
              !isDocumentsLoading && !csDocuments.length ? 'No Credit Submission Documents uploaded yet' : undefined
            }
          >
            {(isDocumentsLoading || csDocuments.length > 0) && (
              <SubmissionDocumentsTable
                documents={csDocuments}
                isLoading={isDocumentsLoading}
                onToggleLenderAccess={toggleLenderAccess}
              />
            )}
          </SectionCard>
        </Stack>

        {/* <SectionCard title="Tasks" sx={{ flex: 1 }}> 
            </SectionCard> */}
        {vp.showTasks === 'show' && (
          <UntypedSpecificTasks
            vo={vo}
            account={account}
            userProfile={userProfile}
            creditSubId={dynamicsCreditSubmissionId}
            lenderContact={(creditSubmission as any).lenderContactInfo}
            submissionsTable={true}
            onTasksLoaded={() => {}}
          />
        )}
      </Box>

      <LenderSubmissionFileUpLoadModal
        open={uploadModalType !== null}
        onClose={() => setUploadModalType(null)}
        refetchDocs={refetch}
        voId={vo.vendorOpportunityId}
        vo={vo}
        lenderContactInfo={(creditSubmission as any).lenderContactInfo}
        relatedTo="submission"
        creditSubmissionId={dynamicsCreditSubmissionId}
      />
    </Box>
  );
};
